import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { api } from './api';
import type { Currency } from './types';

interface CurrencyFormatOptions {
  currency?: Currency | null;
  showSymbol?: boolean;
  decimals?: number;
  convertToDefault?: boolean;
}

interface CurrencyContextValue {
  currency: Currency;
  currencies: Currency[];
  loading: boolean;
  changeCurrency: (currencyCode: string) => void;
  formatCurrency: (amount: number | string | null | undefined, options?: CurrencyFormatOptions) => string;
  convertAmount: (amount: number | string, fromCurrency: Currency, toCurrency?: Currency | null) => number;
  refreshCurrencies: () => Promise<void>;
}

/**
 * Currency Context Provider
 *
 * This context manages currency display and conversion throughout the application.
 *
 * IMPORTANT: Historical Currency Preservation
 * ------------------------------------------
 * The system is designed to preserve historical currency values. When a project, rental,
 * or contract is created, it stores the currency that was active at that time.
 *
 * Backend Protection:
 * - ProjectSerializer, ContractCreateUpdateSerializer, and RentalCreateUpdateSerializer
 *   have been enhanced with update() methods that preserve the existing currency
 * - When updating records, if currency is not explicitly provided, the backend keeps the original
 * - This ensures historical records maintain their currency even if default currency changes
 *
 * When the default currency changes in settings:
 * - Historical records (old projects, rentals, contracts) automatically maintain their ORIGINAL currency (backend enforced)
 * - New records will use the NEW default currency
 * - Display components should pass the record's currency_detail to formatCurrency for proper display
 *
 * Usage Pattern:
 * --------------
 * For historical records (from API with currency_detail field):
 *   formatCurrency(project.budget, { currency: project.currency_detail })
 *
 * For new values or when currency doesn't matter:
 *   formatCurrency(amount)  // Uses default currency
 *
 * For conversion to default currency (rarely needed):
 *   formatCurrency(amount, { currency: originalCurrency, convertToDefault: true })
 */

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }: { children: ReactNode }) => {
  const [currency, setCurrency] = useState<Currency>({
    code: 'CZK',
    name: 'Czech Koruna',
    symbol: 'Kč',
    exchange_rate: 1.0
  });
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDefaultCurrency();
    fetchAllCurrencies();
  }, []);

  const fetchDefaultCurrency = async () => {
    try {
      const response = await api.get<Currency | { data: Currency }>('settings/api/currency/default/');
      const data = 'data' in response ? response.data : response;
      setCurrency({
        code: data.code,
        name: data.name,
        symbol: data.symbol,
        exchange_rate: Number(data.exchange_rate)
      });
    } catch (error) {
      console.error('Error fetching default currency:', error);
      // Keep default CZK if API fails
    } finally {
      setLoading(false);
    }
  };

  const fetchAllCurrencies = async () => {
    try {
      const response = await api.get<Currency[] | { data: Currency[] }>('settings/api/currency/?is_active=true');
      const data = 'data' in response ? response.data : response;
      setCurrencies(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching currencies:', error);
    }
  };

  const changeCurrency = (currencyCode: string) => {
    const newCurrency = currencies.find(c => c.code === currencyCode);
    if (newCurrency) {
      setCurrency({
        code: newCurrency.code,
        name: newCurrency.name,
        symbol: newCurrency.symbol,
        exchange_rate: Number(newCurrency.exchange_rate)
      });
      // Store in localStorage for persistence
      localStorage.setItem('selectedCurrency', currencyCode);
    }
  };

  const formatCurrency = (amount: number | string | null | undefined, options: CurrencyFormatOptions = {}) => {
    const {
      currency: customCurrency = null,
      showSymbol = true,
      decimals = 2,
      convertToDefault = false // New option to control conversion
    } = options;


    // IMPORTANT: If customCurrency is provided, ALWAYS use it (for historical records)
    // Only fall back to default currency if no custom currency is specified
    const activeCurrency = customCurrency !== null && customCurrency !== undefined
      ? customCurrency
      : currency;


    let numAmount = Number(amount) || 0;

    // Only convert if explicitly requested AND currencies are different
    if (convertToDefault && customCurrency && customCurrency.code !== currency.code) {
      numAmount = convertAmount(numAmount, customCurrency, currency);
    }

    // Format with Intl.NumberFormat
    const formatted = new Intl.NumberFormat('cs-CZ', {
      style: 'decimal',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(numAmount);

    // Use the appropriate currency symbol
    const displayCurrency = (convertToDefault && customCurrency) ? currency : activeCurrency;
        return showSymbol ? `${formatted} ${displayCurrency.symbol}` : formatted;
  };

  const convertAmount = (amount: number | string, fromCurrency: Currency, toCurrency: Currency | null = null) => {
    const target = toCurrency || currency;
    const numAmount = Number(amount) || 0;

    // If both currencies are the same, no conversion needed
    if (fromCurrency.code === target.code) {
            return numAmount;
    }

    // Convert to base currency first, then to target currency
    const fromRate = Number(fromCurrency.exchange_rate);
    const toRate = Number(target.exchange_rate);
    const baseAmount = numAmount / fromRate;
    const convertedAmount = baseAmount * toRate;

    return convertedAmount;
  };

  const value = {
    currency,
    currencies,
    loading,
    changeCurrency,
    formatCurrency,
    convertAmount,
    refreshCurrencies: fetchAllCurrencies
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
